import { passwordRequirements } from "@/lib/strapi/verification";
import { Box, PasswordInput, Popover, Progress, Text } from "@mantine/core";
import { CheckRounded, CloseRounded } from "@mui/icons-material";
import { useState } from "react";
import { useInputStyles } from "./FieldTextValue";

type Props = {
  formProps: any;
  label: string;
  editing: boolean;
  disablePopover?: boolean;
};

export default function FieldPasswordValue({
  editing,
  formProps,
  disablePopover,
  label,
}: Props) {
  const { classes } = useInputStyles();
  const [popoverOpened, setPopoverOpened] = useState(false);

  const checks = passwordRequirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(formProps.value)}
    />
  ));
  const strength = getStrength(formProps.value);
  const color =
    strength === 100 ? "correct" : strength > 50 ? "lightblue" : "incorrect";

  return (
    <Popover
      opened={popoverOpened && !disablePopover}
      position="top"
      width="target"
      transitionProps={{ transition: "pop" }}
      withRoles={false}
    >
      <Popover.Target>
        <div
          onFocusCapture={() => setPopoverOpened(true)}
          onBlurCapture={() => setPopoverOpened(false)}
        >
          <PasswordInput
            label={label}
            disabled={!editing}
            size="md"
            variant="default"
            {...formProps}
            classNames={{ input: classes.input, label: classes.label }}
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Progress color={color} value={strength} size={5} mb="xs" />
        {checks}
      </Popover.Dropdown>
    </Popover>
  );
}

function PasswordRequirement({
  meets,
  label,
}: {
  meets: boolean;
  label: string;
}) {
  return (
    <Text
      c={meets ? "correct" : "incorrect"}
      style={{ display: "flex", alignItems: "center" }}
      mt={4}
      size="sm"
    >
      {meets ? (
        <CheckRounded style={{ width: "0.9rem", height: "0.9rem" }} />
      ) : (
        <CloseRounded style={{ width: "0.9rem", height: "0.9rem" }} />
      )}{" "}
      <Box ml={10}>{label}</Box>
    </Text>
  );
}

function getStrength(password: string) {
  let multiplier = password.length > 7 ? 0 : 1;

  passwordRequirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(
    100 - (100 / (passwordRequirements.length + 1)) * multiplier,
    10
  );
}
